import type { IPanelCoordinates } from './vi-dashboard-utility';

/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/ban-ts-comment */
import { BehaviorContainer } from '@eab-eip/hi-behavior-container/hi-behavior-container';
import { AsLitHIC, BehaviorElement, HIP } from '@eab-eip/hi-component/hi-component';
import { stylesTheme } from '@eab-eip/hi-component/hi-component-theme';
import { html, LitElement, css } from 'lit-element';

import dom from '@eab-pip/pi-dom';
import f1 from '@eab-pip/pi-f1';

import { DashboardUtility } from './vi-dashboard-utility';

import './vi-panel';

import { IPanelLayoutState, PanelElement } from './vi-panel';

export const styles = css`
  :host {
    display: block;
    background-color: var(--hi-clr--gray-0);
    border: var(--hi-px-1) solid var(--hi-clr--gray-2);
  }
`;

interface IDashboardLayoutState {
  settings: {
    allow_drag: boolean;
    allow_resize: boolean;
    grid_columns: number;
  };
  panel_layout: {
    panels: IPanelLayoutState[];
  };
}

interface IPanelConfigState {
  panel: PanelElement;
  panelState: IPanelLayoutState;
}

/**
 *
 * @customElement
 * @element vi-dashboard
 *
 * @appliesMixin BehaviorElement
 * @appliesMixin BehaviorContainer
 *
 * @fires remove-ended when a panel is removed
 *
 * @note added max-height: 414px to item-content to support scrolling in 600px fixed panel height
 */
export class DashboardElement extends BehaviorContainer(BehaviorElement(AsLitHIC(LitElement))) {
  static get is() {
    return 'vi-dashboard';
  }

  static get styles() {
    return [stylesTheme, styles];
  }

  /**
   *  gridstack instance
   */
  grid: any;

  /**
   * used for saving and loading panel layouts
   * object, notifies 'layout-state-changed' when layout changes
   *
   * @attr layout-state
   */
  layoutState: IDashboardLayoutState;
  /**
   * default disabled, enable for drag functionality
   *
   * @attr allow-drag
   */
  allowDrag = false;
  /**
   * default disabled, enable for resize functionality
   *
   * @attr allow-resize
   */
  allowResize = false;
  /**
   * number of columns in the grid
   * 2 is the default, can go up to a maximum of 4
   *
   * @attr grid-columns
   */
  gridColumns = 2;
  /**
   * flag for trigger-ended event
   */
  private __flag__isDraggedOrResized = false;

  private __grid__onDragStopOrResizeStopBound: (e: Event) => void;

  static get properties() {
    return {
      allowDrag: {
        type: Boolean,
        attribute: 'allow-drag'
      },
      allowResize: {
        type: Boolean,
        attribute: 'allow-resize'
      },
      gridColumns: {
        type: Number,
        attribute: 'grid-columns'
      },
      layoutState: {
        type: Object,
        attribute: 'layout-state',
        notify: true
      }
    };
  }

  render() {
    return html` <slot id="items"></slot> `;
  }

  connectedCallback() {
    super.connectedCallback();

    this.className = 'grid-stack';
    this.__grid__onDragStopOrResizeStopBound = this.__enableDragResizeUpdate.bind(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.grid) {
      this.grid.off('dragstop resizestop', this.__grid__onDragStopOrResizeStopBound);
      this.grid.destroy();
    }
  }

  updated(props: Map<string | number | symbol, unknown>) {
    super.updated(props);

    this.__initalizeGridStack();

    if (props.has('gridColumns')) {
      this.__reflectGridColumns();
    }
    if (props.has('layoutState') && this.layoutState) {
      this.loadLayoutState(this.layoutState);
    }
    if (props.has('allowDrag') || props.has('allowResize')) {
      this.__engageDragResizeEvents();
    }
  }

  firstUpdated() {
    super.firstUpdated();

    this.__initalizeGridStack();
  }

  /**
   * create a grid stack instance (only once)
   */
  private __initalizeGridStack() {
    //@ts-ignore
    if (!GridStack) {
      return;
    }
    if (this.grid) {
      return;
    }

    this.__grid__createGrid();
    this.__initialize__panel__withGridstack();
    this.__grid__addOnChange();
  }

  /*
   * Initializes gridstack library with the desired configuration
   */
  private __grid__createGrid() {
    //@ts-ignore
    this.grid = GridStack.init({
      disableDrag: !this.allowDrag,
      disableResize: !this.allowResize,
      column: this.__gridColumnsClamped(),
      handleClass: 'panel__toolbar',
      cellHeight: 100,
      margin: 12,
      resizable: {
        handles: 'e,w'
      }
    });
  }

  /**
   * read all panel elements and force those values on each panel
   * @note this method is prone to failing so excessive measures have been taken to stop it from failing
   */
  private __initialize__panel__withGridstack() {
    if (!this.grid) {
      return;
    }
    if (!f1.is.in('getGridItems', this.grid)) {
      return;
    }

    try {
      const gridItems = this.grid.getGridItems();

      f1.each((panelEl) => {
        this.reflectPanel(panelEl as PanelElement);
      }, gridItems);
    } catch (e) {
      return;
    }
  }

  /**
   * gridstack change event
   * when panel properties change either by loading a layout or when a user drags or resizes
   * updates panel layout and dispatches events
   */
  private __grid__addOnChange() {
    if (!this.grid) {
      return;
    }

    this.grid.on('change', (e: Event, items: any) => {
      const isDraggedOrResized = this.__flag__isDraggedOrResized;

      this.__flag__isDraggedOrResized = false;
      if (!isDraggedOrResized) {
        return;
      }

      const gridColumns = this.gridColumns;
      const panels = items.map((item: IPanelCoordinates & { id: string }) => {
        const dimensions = DashboardUtility.coordinatesToDimensions(item, gridColumns);
        const id = f1.read('/el/id', item.id, item);

        return {
          ...dimensions,
          id
        };
      });

      this.loadLayoutState({
        panel_layout: {
          panels
        }
      });
    });
  }

  /**
   * when a panel updates, reflect changes in to GridStack
   */
  reflectPanel(panel: PanelElement) {
    if (!this.grid) {
      return;
    }
    if (!panel) {
      return;
    }

    const panelCoordinates = DashboardUtility.dimensionsToCoordinates(panel, this.gridColumns);
    const attrCoordinates = {
      'gs-x': panelCoordinates.x,
      'gs-y': panelCoordinates.y,
      'gs-h': panelCoordinates.h,
      'gs-w': panelCoordinates.w,
      'gs-id': panel.id
    };

    dom.setAttrs(this, attrCoordinates);
    this.grid.update(panel, panelCoordinates);
    return panel;
  }

  /**
   * for saving the panel layout and grid settings
   * used by events
   */
  saveLayoutState(): IDashboardLayoutState {
    return {
      settings: {
        allow_drag: this.allowDrag,
        allow_resize: this.allowResize,
        grid_columns: this.gridColumns
      },
      panel_layout: {
        panels: f1.map((panelEl: PanelElement) => {
          return panelEl.saveLayoutState();
        }, this.grid.getGridItems() as PanelElement[])
      }
    };
  }

  /**
   * set grid columns and panel layout state
   */
  loadLayoutState(memento: Partial<IDashboardLayoutState>) {
    const gridColumns = f1.read('/settings/grid_columns', this.gridColumns, memento);
    const panels = f1.read('/panel_layout/panels', [], memento);
    const panelState = this.__matchPanelToState(panels);

    this.gridColumns = gridColumns;

    this.__reflectPanelLayoutStates(panelState);
    this.__reflectGridColumns();

    this._dispatchProcess('layout-state-trigger', {
      detail: {
        value: this.saveLayoutState()
      }
    });
  }

  /**
   * match panel layout state to respective panel element by id
   */
  private __matchPanelToState(panels: IPanelLayoutState[]): IPanelConfigState[] {
    if (!this.grid || !panels) {
      return [];
    }

    const slotChildren = this.slotChildren;
    const panelConfigurations = panels.map((panelState: IPanelLayoutState) => {
      const id = panelState.id;
      const panel = f1.find((panelEl: PanelElement | HTMLElement) => panelEl.id === id, slotChildren) as PanelElement;

      return panel ? { panel, panelState } : undefined;
    });

    return f1.compact(panelConfigurations);
  }

  /**
   * loop through matched panel and panel state array and update panels
   */
  private __reflectPanelLayoutStates(panelConfigs: IPanelConfigState[]) {
    f1.each(({ panel, panelState }) => {
      this.__reflectPanelLayoutState(panel, panelState);
    }, panelConfigs);
  }

  /**
   * update panel's properties and gridstack with panel's state
   */
  private __reflectPanelLayoutState(panel: PanelElement, panelState: IPanelLayoutState) {
    if (!DashboardUtility.isEqual(panelState, panel.saveLayoutState())) {
      panel.loadLayoutState(panelState);
    }
  }

  /**
   * update gridstack columns settings
   */
  private __reflectGridColumns() {
    if (this.grid) {
      this.grid.column(this.__gridColumnsClamped(), 'moveScale');
    }
  }

  /**
   * calculate proper grid columns value
   */
  private __gridColumnsClamped() {
    return f1.min(4, f1.max(2, this.gridColumns));
  }

  /**
   * turn on gridstack drag and resize event
   */
  private __engageDragResizeEvents() {
    const grid = this.grid;
    if (!grid) {
      return;
    }

    grid.off('dragstop resizestop', this.__grid__onDragStopOrResizeStopBound);

    if (this.allowDrag || this.allowResize) {
      grid.on('dragstop resizestop', this.__grid__onDragStopOrResizeStopBound);
    }
  }

  /**
   * turn on drag and resize flag
   */
  private __enableDragResizeUpdate(e: Event) {
    this.__flag__isDraggedOrResized = true;
  }
}

export default HIP.factory.element(DashboardElement);
